import { useStorage } from '../useStorage'
import { ETheme } from './types'
import { hexToRgb } from '@/utils'
// import { APP_THEME } from '@/store/mutation-type'
// import { storePrefixKey } from '@/setting/globalSetting'
// import { createLocalStorage } from '@/utils/cache'
import { EGlobalConfig } from '@/enums/cacheEnum'

// const themeStorageKey = `${storePrefixKey}__THEME__`

/** 判断系统是否暗黑模式 */
export function isDarkMode() {
  const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')
  return darkModeQuery.matches
}

// 映射关系
export const themeMap = {
  daily: 'light',
  dark: 'dark',
  automatic: 'auto',
}

/** 当前主题 */
export const theme = ref<ETheme>(useStorage.ls.get(EGlobalConfig.color) || 'dark')

/** 主题 */
export const themeColor = ref<'dark' | 'light'>('dark')

/** 设置主题色 */
export function setPrimaryColor(color?: string) {
  const { getLightColor, getDarkColor } = useChangeColor()

  const el = document.documentElement
  getComputedStyle(el).getPropertyValue('--el-color-primary')

  const themeText = useStorage.ls.get(EGlobalConfig.themeText)
  const { pcFgColor } = themeText || {}

  const insetColor = '#f80000' // color || pcFgColor || '#4008e7'
  el.style.setProperty('--el-color-primary', insetColor)
  el.style.setProperty('--el-color-hover', hexToRgb(insetColor, 0.1))
  el.style.setProperty('--el-color-primary-20', hexToRgb(insetColor, 0.2))
  el.style.setProperty('--el-color-primary-21', hexToRgb(insetColor, 0.3))
  el.style.setProperty('--el-color-primary-0', hexToRgb(insetColor, 0))
  el.style.setProperty('--el-bg-25', hexToRgb(insetColor, 0.5))
  el.style.setProperty('--el-bg-90', hexToRgb(insetColor, 0.9))

  el.style.setProperty('--el-color-primary-dark-2', `${getDarkColor(insetColor, 0.1)}`)
  for (let i = 1; i <= 9; i++) {
    el.style.setProperty(`--el-color-primary-light-${i}`, `${getLightColor(insetColor, i / 10)}`)
  }
}

/**
 * 切换主题
 * dark | light | auto
 */
export function useTheme(value?: ETheme) {
  let v = null
  if (value === 'auto') {
    v = isDarkMode() ? ETheme.DARK : ETheme.LIGHT
  } else {
    v = value
  }

  themeColor.value = v

  document.querySelector('html').className = v
  // createLocalStorage({ prefixKey: themeStorageKey }).set(APP_THEME, v, null)
  useStorage.ls.set(EGlobalConfig.color, v)

  theme.value = value

  return theme
}

/** 初始化主题色 */
export function initTheme() {
  useTheme(theme.value)
  setPrimaryColor()

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    useTheme(ETheme.AUTO)
  })
}

/**
 * 颜色转换函数
 * @method hexToRgb hex 颜色转 rgb 颜色
 * @method rgbToHex rgb 颜色转 Hex 颜色
 * @method getDarkColor 加深颜色值
 * @method getLightColor 变浅颜色值
 */
export function useChangeColor() {
  // str 颜色值字符串
  const hexToRgb = (str: string): any => {
    let hexs: any = ''
    const reg = /^\#?[0-9A-Fa-f]{6}$/
    if (!reg.test(str)) {
      // ElMessage.warning('输入错误的hex')
      return ''
    }
    str = str.replace('#', '')
    hexs = str.match(/../g)
    for (let i = 0; i < 3; i++) hexs[i] = parseInt(hexs[i], 16)
    return hexs
  }
  // r 代表红色 | g 代表绿色 | b 代表蓝色
  const rgbToHex = (r: any, g: any, b: any): string => {
    const reg = /^\d{1,3}$/
    if (!reg.test(r) || !reg.test(g) || !reg.test(b)) {
      // ElMessage.warning('输入错误的rgb颜色值')
      return ''
    }
    const hexs = [r.toString(16), g.toString(16), b.toString(16)]
    for (let i = 0; i < 3; i++) if (hexs[i].length === 1) hexs[i] = `0${hexs[i]}`
    return `#${hexs.join('')}`
  }
  // color 颜色值字符串 | level 变浅的程度，限0-1之间
  const getDarkColor = (color: string, level: number): string => {
    const reg = /^\#?[0-9A-Fa-f]{6}$/
    if (!reg.test(color)) {
      // ElMessage.warning('输入错误的hex颜色值')
      return ''
    }
    const rgb = useChangeColor().hexToRgb(color)
    for (let i = 0; i < 3; i++) rgb[i] = Math.floor(rgb[i] * (1 - level))
    return useChangeColor().rgbToHex(rgb[0], rgb[1], rgb[2])
  }
  // color 颜色值字符串 | level 加深的程度，限0-1之间
  const getLightColor = (color: string, level: number): string => {
    const reg = /^\#?[0-9A-Fa-f]{6}$/
    if (!reg.test(color)) {
      // ElMessage.warning('输入错误的hex颜色值')
      return ''
    }
    const rgb = useChangeColor().hexToRgb(color)
    for (let i = 0; i < 3; i++) rgb[i] = Math.floor((255 - rgb[i]) * level + rgb[i])
    return useChangeColor().rgbToHex(rgb[0], rgb[1], rgb[2])
  }
  return {
    hexToRgb,
    rgbToHex,
    getDarkColor,
    getLightColor,
  }
}

